// Präsentationsmodus (Pflichtenheft §20): geführter Kunden-Modus mit 7 Seiten.
// Vollbild-Overlay, große Typografie, verständlich statt fachlich. PFLICHT (§20):
// Risiken werden NICHT verschwiegen — ein durchgängiger Vertrauensfaktor-/Entwurf-
// Hinweis ist immer sichtbar. Reine Anzeige aus /presentation (+ Varianten best-effort).
import { useEffect, useState } from "react";
import { apiClient } from "@/lib/api-client.js";

interface Summary {
  latestSimulation?: { specificYieldKwhPerKwp?: number | null; annualYieldKwh?: number | null; confidenceScore?: number | null } | null;
  latestEconomics?: { paybackYears?: number | null; annualSavingsEurExpected?: number | null; npvEur?: number | null; roi?: number | null; co2SavedKgPerYear?: number | null } | null;
  latestEngineering?: { modulesTotal?: number | null; numberOfStrings?: number | null } | null;
  energyScore?: { score?: number; label?: string; explanation?: string } | null;
}
interface VariantCard { id: string; label: string; autarkieProzent: number; amortisationJahre: number | null; empfohlen?: boolean; foerderungEur?: number }
interface VariantsResp { detail?: { variants?: Array<{ id: string; label: string; autarkieProzent: number; amortisationMitFoerderungJahre: number | null; amortisationJahre: number | null; foerderungEur: number }>; empfehlungId?: string } }

const eur = (n?: number | null) => (n == null ? "—" : Math.round(n).toLocaleString("de-AT") + " €");
const num = (n?: number | null, d = 0) => (n == null ? "—" : n.toLocaleString("de-AT", { maximumFractionDigits: d }));

export function PresentationMode({
  projectId, projectName, customerName, address, onClose,
}: { projectId: string; projectName: string; customerName: string; address: string; onClose: () => void }) {
  const [page, setPage] = useState(0);
  const [sum, setSum] = useState<Summary | null>(null);
  const [variants, setVariants] = useState<VariantCard[] | null>(null);

  useEffect(() => {
    void (async () => {
      const res = await apiClient.get<Summary>(`/projects/${projectId}/presentation`);
      if (res.success) setSum(res.data);
    })();
  }, [projectId]);

  const loadVariants = async () => {
    if (variants) return;
    const res = await apiClient.post<VariantsResp>(`/projects/${projectId}/konzept/variants`, {});
    if (res.success && res.data.detail?.variants) {
      setVariants(res.data.detail.variants.map((v) => ({
        id: v.id, label: v.label, autarkieProzent: v.autarkieProzent,
        amortisationJahre: v.amortisationMitFoerderungJahre ?? v.amortisationJahre,
        empfohlen: v.id === res.data.detail?.empfehlungId, foerderungEur: v.foerderungEur,
      })));
    } else setVariants([]);
  };
  useEffect(() => { if (page === 4) void loadVariants(); /* eslint-disable-next-line */ }, [page]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") setPage((p) => Math.min(6, p + 1));
      else if (e.key === "ArrowLeft") setPage((p) => Math.max(0, p - 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const sim = sum?.latestSimulation, eco = sum?.latestEconomics, eng = sum?.latestEngineering, score = sum?.energyScore;
  const kwp = eng?.modulesTotal ? (eng.modulesTotal * 0.43) : null;
  const confPct = sim?.confidenceScore != null ? Math.round(sim.confidenceScore * 100) : null;

  const PAGES = ["Ihr Gebäude", "Empfohlene Lösung", "Energiefluss", "Wirtschaftlichkeit", "Variantenvergleich", "Warum diese Empfehlung?", "Ihre nächsten Schritte"];

  const Tile = ({ label, value, unit }: { label: string; value: string; unit?: string }) => (
    <div style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 12, padding: "20px 24px", textAlign: "center", minWidth: 160 }}>
      <div style={{ fontSize: 40, fontWeight: 800, color: "#7ef0a6", lineHeight: 1.1 }}>{value}<span style={{ fontSize: 18, color: "#cbd5e1", marginLeft: 4 }}>{unit}</span></div>
      <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 6 }}>{label}</div>
    </div>
  );

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 2000, background: "linear-gradient(160deg,#0b1a2e 0%,#12243d 100%)", color: "#e2e8f0", display: "flex", flexDirection: "column", fontFamily: "Arial, sans-serif" }}>
      {/* Kopf */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 28px", borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
        <div style={{ fontSize: 18, fontWeight: 800 }}>Energy<span style={{ color: "#7ef0a6" }}>OS</span> · Präsentation</div>
        <div style={{ fontSize: 13, color: "#94a3b8" }}>{customerName} · {projectName}</div>
        <button onClick={onClose} style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.4)", color: "#fff", borderRadius: 20, padding: "6px 16px", cursor: "pointer", fontSize: 13 }}>Schließen ✕</button>
      </div>

      {/* Seiteninhalt */}
      <div style={{ flex: 1, overflowY: "auto", padding: "40px 6vw", display: "flex", flexDirection: "column", justifyContent: "center" }}>
        <div style={{ fontSize: 13, color: "#7ef0a6", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 8 }}>{page + 1} / 7 · {PAGES[page]?.toUpperCase()}</div>

        {page === 0 && (
          <div>
            <h1 style={{ fontSize: 44, fontWeight: 800, margin: "0 0 12px" }}>Ihr Gebäude</h1>
            <p style={{ fontSize: 20, color: "#cbd5e1", maxWidth: 720 }}>{address}</p>
            <p style={{ fontSize: 16, color: "#94a3b8", marginTop: 20, maxWidth: 720 }}>Wir haben Ihr Dach vermessen und die nutzbare Fläche für eine Photovoltaik-Anlage ermittelt. Auf den nächsten Seiten sehen Sie die empfohlene Lösung und was sie Ihnen bringt.</p>
          </div>
        )}

        {page === 1 && (
          <div>
            <h1 style={{ fontSize: 40, fontWeight: 800, margin: "0 0 24px" }}>Empfohlene Lösung</h1>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              <Tile label="PV-Leistung" value={kwp != null ? num(kwp, 1) : "—"} unit="kWp" />
              <Tile label="Module" value={num(eng?.modulesTotal)} unit="Stk" />
              <Tile label="Jahresertrag" value={num(sim?.annualYieldKwh)} unit="kWh" />
              {/* Energy Score nur zeigen, wenn wirklich berechnet — sonst echten Ertragswert statt „—/100" vor dem Kunden. */}
              {score?.score != null
                ? <Tile label="Energy Score" value={String(score.score)} unit="/100" />
                : <Tile label="Spezifischer Ertrag" value={num(sim?.specificYieldKwhPerKwp)} unit="kWh/kWp" />}
            </div>
          </div>
        )}

        {page === 2 && (
          <div>
            <h1 style={{ fontSize: 40, fontWeight: 800, margin: "0 0 24px" }}>Ihr Energiefluss</h1>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              <Tile label="Erzeugung/Jahr" value={num(sim?.annualYieldKwh)} unit="kWh" />
              <Tile label="Spezifischer Ertrag" value={num(sim?.specificYieldKwhPerKwp)} unit="kWh/kWp" />
              <Tile label="CO₂-Einsparung/Jahr" value={eco?.co2SavedKgPerYear != null ? num(eco.co2SavedKgPerYear / 1000, 1) : "—"} unit="t" />
            </div>
            <p style={{ fontSize: 15, color: "#94a3b8", marginTop: 24, maxWidth: 720 }}>Ihre Anlage erzeugt sauberen Strom direkt auf Ihrem Dach. Was Sie selbst verbrauchen, müssen Sie nicht teuer zukaufen — der Rest wird ins Netz eingespeist.</p>
          </div>
        )}

        {page === 3 && (
          <div>
            <h1 style={{ fontSize: 40, fontWeight: 800, margin: "0 0 24px" }}>Wirtschaftlichkeit</h1>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              <Tile label="Ersparnis pro Jahr" value={eco?.annualSavingsEurExpected != null ? num(eco.annualSavingsEurExpected) : "—"} unit="€" />
              <Tile label="Amortisation" value={eco?.paybackYears != null ? num(eco.paybackYears, 1) : "—"} unit="Jahre" />
              <Tile label="Gewinn über Laufzeit (NPV)" value={eco?.npvEur != null ? num(eco.npvEur) : "—"} unit="€" />
            </div>
            <p style={{ fontSize: 14, color: "#94a3b8", marginTop: 24 }}>Alle Werte sind Richtwerte auf Basis Ihrer Angaben — kein verbindliches Angebot.</p>
          </div>
        )}

        {page === 4 && (
          <div>
            <h1 style={{ fontSize: 40, fontWeight: 800, margin: "0 0 24px" }}>Ihre Varianten im Vergleich</h1>
            {variants == null ? <p style={{ color: "#94a3b8" }}>Lade Varianten…</p> : variants.length === 0 ? (
              <p style={{ color: "#94a3b8" }}>Für den Variantenvergleich werden Anlagengröße und Jahresverbrauch benötigt.</p>
            ) : (
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                {variants.map((v) => (
                  <div key={v.id} style={{ background: v.empfohlen ? "rgba(126,240,166,0.1)" : "rgba(255,255,255,0.06)", border: `1px solid ${v.empfohlen ? "#7ef0a6" : "rgba(255,255,255,0.12)"}`, borderRadius: 12, padding: "18px 22px", minWidth: 200 }}>
                    <div style={{ fontSize: 18, fontWeight: 700 }}>{v.label}{v.empfohlen && <span style={{ marginLeft: 8, fontSize: 10, background: "#7ef0a6", color: "#0b1a2e", padding: "2px 8px", borderRadius: 10, fontWeight: 800 }}>EMPFOHLEN</span>}</div>
                    <div style={{ marginTop: 12, fontSize: 14, color: "#cbd5e1" }}>Autarkie: <strong style={{ color: "#7ef0a6" }}>{v.autarkieProzent} %</strong></div>
                    <div style={{ fontSize: 14, color: "#cbd5e1" }}>Amortisation: {v.amortisationJahre != null ? `~${num(v.amortisationJahre, 1)} J` : "—"}</div>
                    {v.foerderungEur ? <div style={{ fontSize: 13, color: "#7ef0a6", marginTop: 6 }}>bis {eur(v.foerderungEur)} Förderung</div> : null}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {page === 5 && (
          <div>
            <h1 style={{ fontSize: 40, fontWeight: 800, margin: "0 0 20px" }}>Warum diese Empfehlung?</h1>
            <p style={{ fontSize: 18, color: "#cbd5e1", maxWidth: 760 }}>{score?.explanation ?? "Die empfohlene Lösung bietet das beste Verhältnis aus Unabhängigkeit, Wirtschaftlichkeit und Zukunftssicherheit für Ihr Gebäude."}</p>
            <ul style={{ fontSize: 16, color: "#cbd5e1", marginTop: 20, lineHeight: 1.8 }}>
              <li>Weniger Abhängigkeit von steigenden Strompreisen</li>
              <li>Attraktive Bundes- und Landesförderungen</li>
              <li>Saubere Energie, weniger CO₂</li>
              <li>Wertsteigerung Ihrer Immobilie</li>
            </ul>
          </div>
        )}

        {page === 6 && (
          <div>
            <h1 style={{ fontSize: 40, fontWeight: 800, margin: "0 0 24px" }}>Ihre nächsten Schritte</h1>
            <ol style={{ fontSize: 18, color: "#cbd5e1", lineHeight: 2, maxWidth: 640 }}>
              <li>Unverbindliche Beratung — wir klären Ihre Fragen.</li>
              <li>Vor-Ort-Besichtigung von Dach und Elektrik.</li>
              <li>Verbindliches Angebot mit Fixpreisen.</li>
              <li>Professionelle Installation durch zertifizierte Elektriker.</li>
            </ol>
          </div>
        )}
      </div>

      {/* §20-PFLICHT: Risiken NICHT verschweigen — Vertrauensfaktor + Entwurf immer sichtbar */}
      <div style={{ padding: "8px 28px", background: "rgba(0,0,0,0.25)", fontSize: 12, color: "#94a3b8", textAlign: "center" }}>
        {confPct != null ? `Vertrauensfaktor der Berechnung: ${confPct} %. ` : "Werte sind Richtwerte. "}
        Alle Angaben sind ein <strong style={{ color: "#cbd5e1" }}>Entwurf, keine Zusage</strong> — endgültige Werte nach Vor-Ort-Prüfung.
      </div>

      {/* Navigation */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 28px", borderTop: "1px solid rgba(255,255,255,0.1)" }}>
        <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.3)", color: page === 0 ? "#475569" : "#fff", borderRadius: 20, padding: "8px 20px", cursor: page === 0 ? "default" : "pointer" }}>← Zurück</button>
        <div style={{ display: "flex", gap: 8 }}>
          {PAGES.map((_, i) => (
            <button key={i} onClick={() => setPage(i)} aria-label={`Seite ${i + 1}`} style={{ width: 10, height: 10, borderRadius: "50%", border: "none", cursor: "pointer", background: i === page ? "#7ef0a6" : "rgba(255,255,255,0.25)" }} />
          ))}
        </div>
        <button onClick={() => setPage((p) => Math.min(6, p + 1))} disabled={page === 6} style={{ background: page === 6 ? "transparent" : "#2563eb", border: "none", color: page === 6 ? "#475569" : "#fff", borderRadius: 20, padding: "8px 24px", cursor: page === 6 ? "default" : "pointer", fontWeight: 600 }}>Weiter →</button>
      </div>
    </div>
  );
}
